/**
 * El contrato OpenAPI → lo que la página de documentación pinta.
 *
 * Vive aparte de la pantalla porque es lo único de ella que puede equivocarse
 * sin que se note: agrupar mal, perder una ruta al agrupar, o resolver mal un
 * `$ref` y enseñar un cuerpo de ejemplo que no corresponde. Una ruta que
 * desaparece de la documentación es peor que una mal maquetada — nadie la echa
 * de menos.
 *
 * Aquí no se escribe ni una frase sobre lo que hace la API: todo el texto viene
 * del backend, que lo genera desde su tabla de rutas.
 */

export interface Contrato {
  info: { title: string; version: string; description?: string };
  servers?: { url: string }[];
  tags?: { name: string; "x-titulo"?: string; description?: string }[];
  "x-guia": Guia;
  /** El SDK oficial. Puede no venir: un servidor anterior no lo publica. */
  "x-sdk"?: Sdk;
  components?: { schemas?: Record<string, Esquema> };
  paths: Record<string, Record<string, OperacionCruda>>;
}

/**
 * El SDK oficial, tal como lo publica el backend (SPEC-027).
 *
 * Nada de esto se escribe en el panel, igual que la guía: son hechos del
 * contrato y viven junto al código que los produce. Los ejemplos, además, son el
 * contenido literal de archivos que una prueba ejecuta — así que lo que se pinta
 * aquí es código que funciona, no una ilustración.
 */
export interface Sdk {
  titulo: string;
  /** Los lenguajes. Puede no venir: un servidor anterior no lo publica. */
  subtitulo?: string;
  entrada: string;
  garantia: string;
  paquetes: {
    lenguaje: string;
    nombre: string;
    instalar: string;
    requiere: string;
    codigo?: string;
    licencia: string;
  }[];
  puertos: { nombre: string; que: string; porque: string }[];
  limites: string[];
  ejemplos: { titulo: string; lenguaje: string; archivo: string; codigo: string }[];
}

export interface Guia {
  /** Qué es esto, y qué tiene que aguantar tu sistema. Va antes que las rutas. */
  apertura?: {
    titulo: string;
    entrada: string;
    direcciones: { nombre: string; que: string; detalle: string; rutas: string }[];
    advertencia: { titulo: string; cuerpo: string };
  };
  autenticacion: { titulo: string; cuerpo: string; codigo?: string }[];
  tenerEnCuenta: string[];
  estados: { titulo: string; entrada: string; filas: string[][] };
  errores: { titulo: string; filas: string[][] };
}

interface Esquema {
  required?: string[];
  properties?: Record<string, { title?: string; description?: string }>;
}

interface OperacionCruda {
  summary?: string;
  description?: string;
  tags?: string[];
  "x-permiso"?: string;
  "x-ejemplo"?: Record<string, unknown>;
  security?: unknown[];
  parameters?: { name: string; required?: boolean }[];
  requestBody?: { content?: Record<string, { schema?: { $ref?: string } }> };
  responses?: Record<string, { description?: string }>;
}

export interface Operacion {
  metodo: string;
  ruta: string;
  resumen: string;
  descripcion?: string;
  permiso?: string;
  publica: boolean;
  exigeCliente: boolean;
  /** El esquema del cuerpo, ya resuelto. `null` si la ruta no recibe cuerpo. */
  cuerpo: Record<string, unknown> | null;
  obligatorios: string[];
  ejemplo?: Record<string, unknown>;
  respuestas: [string, string][];
}

export interface Familia {
  id: string;
  titulo: string;
  entrada: string;
  operaciones: Operacion[];
}

const ORDEN_METODO = ["GET", "POST", "PUT", "DELETE"];

/** Un ancla estable por operación: no puede depender del orden de la lista. */
export const anclaDe = (op: { metodo: string; ruta: string }) =>
  `${op.metodo}-${op.ruta}`.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

/**
 * Resuelve un `$ref` local a un ejemplo legible del cuerpo.
 *
 * Se devuelve la forma, no los valores: `{ "cliente": "string" }` dice qué se
 * espera. Cuando el backend manda `x-ejemplo`, ese gana — un ejemplo real vale
 * más que una plantilla de tipos.
 */
function cuerpoDe(op: OperacionCruda, esquemas: Record<string, Esquema>) {
  const ref = op.requestBody?.content?.["application/json"]?.schema?.$ref;
  if (!ref) return { cuerpo: null, obligatorios: [] as string[] };

  const nombre = ref.split("/").pop() ?? "";
  const esquema = esquemas[nombre];
  if (!esquema?.properties) return { cuerpo: null, obligatorios: [] };

  const cuerpo: Record<string, unknown> = {};
  for (const [campo, def] of Object.entries(esquema.properties)) {
    cuerpo[campo] = def.description?.includes("Formato colombiano")
      ? "1.240.000"
      : def.description?.includes("ISO 8601")
        ? "2026-09-03"
        : "string";
  }
  return { cuerpo, obligatorios: esquema.required ?? [] };
}

/**
 * Agrupa las operaciones por familia, en el orden que declara el contrato.
 *
 * Una operación sin familia declarada NO se descarta: cae en «Otros». Perderla
 * en silencio sería documentación incompleta que nadie detecta, que es
 * exactamente lo que esta página existe para evitar.
 */
export function agrupar(contrato: Contrato): Familia[] {
  const esquemas = contrato.components?.schemas ?? {};
  const declaradas = contrato.tags ?? [];

  const familias = new Map<string, Familia>();
  for (const t of declaradas) {
    familias.set(t.name, {
      id: t.name,
      titulo: t["x-titulo"] ?? t.name,
      entrada: t.description ?? "",
      operaciones: [],
    });
  }

  for (const [ruta, metodos] of Object.entries(contrato.paths)) {
    for (const [metodoCrudo, cruda] of Object.entries(metodos)) {
      const metodo = metodoCrudo.toUpperCase();
      const { cuerpo, obligatorios } = cuerpoDe(cruda, esquemas);

      const op: Operacion = {
        metodo,
        ruta,
        resumen: cruda.summary ?? ruta,
        descripcion: cruda.description,
        permiso: cruda["x-permiso"],
        publica: Array.isArray(cruda.security) && cruda.security.length === 0,
        exigeCliente: (cruda.parameters ?? []).some((p) => p.name === "X-Recauda-Cliente"),
        cuerpo,
        obligatorios,
        ejemplo: cruda["x-ejemplo"],
        respuestas: Object.entries(cruda.responses ?? {})
          .map(([codigo, r]) => [codigo, r.description ?? ""] as [string, string])
          .sort((a, b) => Number(a[0]) - Number(b[0])),
      };

      const id = cruda.tags?.[0] ?? "otros";
      if (!familias.has(id)) {
        familias.set(id, { id, titulo: "Otros", entrada: "", operaciones: [] });
      }
      familias.get(id)!.operaciones.push(op);
    }
  }

  return [...familias.values()]
    .filter((f) => f.operaciones.length > 0)
    .map((f) => ({
      ...f,
      // Dentro de una familia, los GET antes que lo que escribe: se lee para
      // entender y se escribe después.
      operaciones: f.operaciones.sort(
        (a, b) =>
          ORDEN_METODO.indexOf(a.metodo) - ORDEN_METODO.indexOf(b.metodo) ||
          a.ruta.localeCompare(b.ruta),
      ),
    }));
}
